<script setup lang="ts">
    import { RouterView, useRoute } from 'vue-router';
    import { useLanguage } from '@/composables/useLanguage';
    import useGlobalToast, { GLOBAL_TOAST_GROUP_NAME } from '@/composables/useGlobalToast';
    import PortalFooter from '@/components/PortalFooter.vue';
    import Toast from 'primevue/toast';
    import ConfirmPopup from 'primevue/confirmpopup';
    import CartSidebar from './components/cart/CartSidebar.vue';
    import { HttpStatusCode, isAxiosError } from 'axios';
    import { addGlobalVueErrorHandler } from '@/logger/global-error-handler-vue';
    import { useAuthenticationAction } from '@/composables/authentication';
    import StageBadge from '@/components/StageBadge.vue';
    import OrderInfoSidebar from '@/components/catalog-settings/OrderInfoSidebar.vue';
    import { watch } from 'vue';
    import { stringValueOfParam } from '@/util/param';
    import MarketSwitchConfirmation from '@/components/navigation/MarketSwitchConfirmation.vue';
    import { useProvisionTypeQuery } from './composables/provision-type';
    import AppUpdateNotificationModal from '@/components/AppUpdateNotificationModal.vue';
    import { APP_UPDATE_NOTIFICATION_DIALOG } from '@/constants.ts';
    import { useI18n } from 'vue-i18n';
    import { useConfirm } from 'primevue/useconfirm';
    import { useHead } from '@unhead/vue';

    const { t } = useI18n();
    const { setLanguage } = useLanguage();
    const { errorToastOptions, addToast } = useGlobalToast();
    const authenticationAction = useAuthenticationAction();
    const route = useRoute();
    const confirm = useConfirm();
    const { currentProvisionType } = useProvisionTypeQuery();

    let hasNewVersion = false;
    let hasErrorForcingReload = false;

    const updateCheckIntervalSeconds = 60;
    const appUpdateInterval = setInterval(
        () => void periodicallyCheckForAppUpdate(),
        updateCheckIntervalSeconds * 1000
    );

    addGlobalVueErrorHandler((error) => {
        if (isAxiosError(error) && error.response?.status === HttpStatusCode.Unauthorized) {
            addToast({
                ...errorToastOptions,
                summary: 'ERROR.SESSION_EXPIRED.SUMMARY',
                detail: 'ERROR.SESSION_EXPIRED.DETAIL',
            });
            authenticationAction.deleteExpiredSession();
        }
    });

    addGlobalVueErrorHandler((error) => {
        if (hasNewVersion && isAxiosError(error)) {
            hasErrorForcingReload = true;
            void openAppUpdateDialogWithoutCancel();
        }
    });

    watch(
        () => route.params.language,
        async (newLanguage) => {
            await setLanguage(stringValueOfParam(newLanguage));
        }
    );

    async function periodicallyCheckForAppUpdate(): Promise<void> {
        if (hasErrorForcingReload) {
            return;
        }

        const versionMetaTag: Element | null = document.querySelector('meta[name="VERSION_HASH"]');
        const currentVersion = versionMetaTag?.attributes?.getNamedItem('content')?.value?.trim();
        if (currentVersion == null || currentVersion.length === 0) {
            return;
        }

        //TODO This is just a band-aid fix to ensure the call to /VERSION does not get cached by the browser. Find a proper fix for it later.
        const time = new Date().getTime().toString();
        const response = await fetch(`/VERSION?t=${time}`);
        const lastDeployedVersion = (await response.text())?.trim();
        // eslint-disable-next-line no-magic-numbers
        if (response.status !== 200 || lastDeployedVersion == null || lastDeployedVersion.length === 0) {
            return;
        }

        const lastVersionIsSameAsCurrentVersion = lastDeployedVersion === currentVersion.trim();
        if (lastVersionIsSameAsCurrentVersion) {
            return;
        }

        hasNewVersion = true;
        if (!hasErrorForcingReload) {
            await openAppUpdateDialogWithCancel();
        }
    }

    async function openAppUpdateDialogWithCancel(): Promise<void> {
        await openAppUpdateDialog(true);
    }

    async function openAppUpdateDialogWithoutCancel(): Promise<void> {
        await openAppUpdateDialog(false);
    }

    async function openAppUpdateDialog(canReject: boolean): Promise<void> {
        if (!canReject) {
            stopPeriodicallyCheckingForUpdate();
        }

        return new Promise<void>((resolve) => {
            confirm.require({
                group: APP_UPDATE_NOTIFICATION_DIALOG,
                header: t('APP_UPDATE_NOTIFICATION_DIALOG.HEADER'),
                message: canReject
                    ? t('APP_UPDATE_NOTIFICATION_DIALOG.MESSAGE')
                    : t('APP_UPDATE_NOTIFICATION_DIALOG.MESSAGE_NO_REJECT'),
                acceptClass: 'p-button p-component',
                rejectClass: canReject ? 'p-button p-component p-button-outlined' : 'invisible',
                rejectLabel: t('APP_UPDATE_NOTIFICATION_DIALOG.REJECT_LABEL'),
                acceptLabel: t('APP_UPDATE_NOTIFICATION_DIALOG.ACCEPT_LABEL'),
                accept: () => window.location.reload(),
                reject: () => resolve(),
            });
        });
    }

    function stopPeriodicallyCheckingForUpdate(): void {
        clearInterval(appUpdateInterval);
    }

    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        // prevent auto-zoom for input[text] on iOS devices only (maximum-scale=1 would breaks pinch-zoom on Android devices)
        useHead({
            meta: [
                {
                    name: 'viewport',
                    content: 'width=device-width, initial-scale=1.0, maximum-scale=1',
                },
            ],
        });
    }
</script>

<template>
    <RouterView />

    <PortalFooter />
    <Toast position="bottom-center" :group="GLOBAL_TOAST_GROUP_NAME" />
    <MarketSwitchConfirmation />
    <AppUpdateNotificationModal />
    <ConfirmPopup />
    <CartSidebar />
    <OrderInfoSidebar v-if="currentProvisionType != null" />
    <StageBadge></StageBadge>
</template>
<style lang="scss">
    #app-update-notification-dialog {
        .p-dialog-close-button,
        .p-confirmdialog-reject-button.invisible {
            display: none;
        }
    }
</style>
